import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import { ToastMessages } from "../../../../enums/toastMessages";

export const useFileUpload = () => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (
    file: File,
    allowedTypes: string[] = [".csv", ".pdf", ".xlsx", ".xls"],
    maxSizeMB: number = 10,
  ): boolean => {
    // Define MIME types for each file type
    const mimeTypes: { [key: string]: string[] } = {
      ".csv": ["text/csv", "application/csv"],
      ".pdf": ["application/pdf"],
      ".xlsx": [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
      ".xls": ["application/vnd.ms-excel"],
    };

    // Validate file type
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const isValidExtension = allowedTypes.some(
      (type) => fileExtension === type,
    );
    const isValidMimeType = allowedTypes.some((type) =>
      mimeTypes[type]?.includes(file.type),
    );

    if (!isValidExtension && !isValidMimeType) {
      toast.error(ToastMessages.CASE_INITIATION_NO_FILE);
      return false;
    }

    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(ToastMessages.CASE_INITIATION_FILE_SIZE_ERROR);
      return false;
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    uploadedFile,
    fileInputRef,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDeleteFile,
  };
};
